import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Trash2, Edit3, Send, Calendar, Building, User, Search, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProposalDrafts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ad-proposals/drafts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrafts(response.data);
    } catch (error) {
      console.error('Error fetching drafts:', error);
      toast.error('Failed to fetch drafts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDraft = (draft) => {
    // Store draft data in sessionStorage and navigate to proposal page
    sessionStorage.setItem('proposalDraft', JSON.stringify({
      ...draft,
      action: 'edit_draft',
      draft_id: draft.id
    }));
    navigate('/advertisement-proposal');
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/ad-proposals/drafts/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Draft deleted');
      fetchDrafts();
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDrafts = drafts.filter(draft => 
    draft.draft_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    draft.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    draft.contact_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      <Header />
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
              <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
                📝 Saved Drafts
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" onClick={fetchDrafts}>
                <RefreshCw className="mr-2" size={16} />
                Refresh
              </Button>
              <Button onClick={() => navigate('/advertisement-proposal')} style={{ backgroundColor: '#7c3aed' }}>
                <FileText className="mr-2" size={16} />
                New Proposal
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
            <div className="flex items-center space-x-4">
              <Search className="text-gray-400" size={20} />
              <Input
                placeholder="Search drafts by name, company, or contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <p className="text-gray-500 text-sm">Total Drafts</p>
              <p className="text-3xl font-bold text-purple-600">{drafts.length}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <p className="text-gray-500 text-sm">Aabkari Times</p>
              <p className="text-3xl font-bold text-blue-600">
                {drafts.filter(d => d.magazine === 'Aabkari Times' || d.magazine === 'Aabkari Times ENG').length}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <p className="text-gray-500 text-sm">Sugar Times</p>
              <p className="text-3xl font-bold text-green-600">
                {drafts.filter(d => d.magazine === 'Sugar Times').length}
              </p>
            </div>
          </div>

          {/* Drafts List */}
          <div className="bg-white rounded-lg shadow-sm border">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading drafts...</p>
              </div>
            ) : filteredDrafts.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="mx-auto text-gray-300 mb-4" size={64} />
                <p className="text-gray-500 text-lg">No drafts saved yet</p>
                <p className="text-gray-400 text-sm mt-2">
                  When you copy a sent proposal and save as draft, it will appear here.
                </p>
                <Button 
                  onClick={() => navigate('/ad-proposal-reports')} 
                  variant="outline" 
                  className="mt-4"
                >
                  View Sent Proposals
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {filteredDrafts.map(draft => (
                  <div 
                    key={draft.id} 
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-semibold text-gray-800">
                            {draft.draft_name || 'Untitled Draft'}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            (draft.magazine === 'Aabkari Times' || draft.magazine === 'Aabkari Times ENG') 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {draft.magazine}
                          </span>
                        </div>
                        
                        <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                          {draft.company_name && (
                            <span className="flex items-center">
                              <Building size={14} className="mr-1" />
                              {draft.company_name}
                            </span>
                          )}
                          {draft.contact_name && (
                            <span className="flex items-center">
                              <User size={14} className="mr-1" />
                              {draft.contact_name}
                            </span>
                          )}
                          <span className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            {formatDate(draft.updated_at)}
                          </span>
                        </div>
                        
                        {draft.copied_from_company && (
                          <p className="text-xs text-purple-600 mt-1">
                            📋 Copied from: {draft.copied_from_company}
                          </p>
                        )}
                        
                        {draft.ad_positions && draft.ad_positions.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            {draft.ad_positions.length} position(s) • 
                            Total: ₹{draft.ad_positions.reduce((sum, p) => sum + (p.discounted_price || p.std_price || 0), 0).toLocaleString()}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleOpenDraft(draft)}
                          style={{ backgroundColor: '#7c3aed' }}
                        >
                          <Edit3 size={14} className="mr-1" />
                          Edit & Send
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleDeleteDraft(draft.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="mt-6 bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2">About Drafts</h3>
            <ul className="text-sm text-purple-700 space-y-1">
              <li>• Drafts are auto-saved when you copy a sent proposal and choose &quot;Save as Draft&quot;</li>
              <li>• Drafts do not affect the original sent proposals</li>
              <li>• You can edit and send drafts at any time</li>
              <li>• Once sent, the draft becomes a new sent proposal</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalDrafts;
